import * as Yup from "yup";
import Box from "@material-ui/core/Box";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import HelpIcon from "@material-ui/icons/Help";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { ForwardRefRenderFunction } from "react";
import { Field, Form, Formik } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch } from "react-redux";

import BaseInput from "../../../components/elements/BaseInput";
import FormFieldError from "../../../components/elements/FormFieldError";
import Select from "../../../components/elements/Select";
import type { CampaignBuilderMode } from "../../../components/redesign/modules/CampaignBuilderForm";
import { MAX_CAMPAIGN_NAME_LENGTH } from "../../../constants/campaignValidation";
import { setCampaignBasicDetails, setCampaignTemplateName } from "../../../redux/reducers/campaignToCreate";

const tooltips = {
  showInHubspot:
    "Checking this option will allow a HubSpot user to add and remove a Company to the Campaign. This will be available to All HubSpot users within your account who have permissions to access the Company Record",
  mediaType: (
    <>
      <Typography color="inherit">Please Note</Typography>
      <br />
      Currently, you can only choose display campaigns (image/gif) <em>or</em> video campaigns.
      <br />
      <br />
      <b>If you select video,</b> you will only be able to upload video creatives later in the campaign creation
      process.
    </>
  ),
  maxCompanies:
    "The maximum number of target companies that can be added to this campaign. If left empty or at 0, no limit will be imposed.",
};

const mediaTypeOptions = [
  { label: "Display", value: "display" },
  { label: "Video", value: "video" },
];

export type FormInitialValues = {
  templateName: string;
  name: string;
  description: string;
  mediaType: "video" | "display";
  showInHubspot: boolean;
  maxCompanies: number | string;
};

type FormProps = {
  formInitialValues: FormInitialValues;
  handleSubmit: (values: FormInitialValues) => void;
  mode?: CampaignBuilderMode;
};

type Ref = HTMLButtonElement;

// ── Styled components ─────────────────────────────────────────────────────────

const FormColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const FieldGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const FieldLabel = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  color: theme.palette.brand.gray500,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
}));

const RequiredMark = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.coral,
}));

const MediaRow = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  alignItems: "flex-start",
});

const FieldRow = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: 6,
});

const InputSkin = styled("div")(({ theme }: { theme: Theme }) => ({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    background: theme.palette.brand.white,
    borderRadius: 8,
    fontSize: 13,
    color: theme.palette.brand.gray900,
    "& fieldset": {
      border: `1px solid ${theme.palette.brand.gray200}`,
      borderRadius: 8,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.brand.gray300,
    },
    "&.Mui-focused fieldset": {
      border: `1px solid ${theme.palette.brand.blueAccent}`,
    },
    "&.Mui-error fieldset": {
      border: `1px solid ${theme.palette.brand.errorRed}`,
    },
    "&.Mui-disabled": {
      background: theme.palette.brand.gray50,
      "& fieldset": {
        borderColor: theme.palette.brand.gray200,
      },
    },
  },
}));

const TooltipIcon = styled(HelpIcon)(({ theme }: { theme: Theme }) => ({
  fontSize: 18,
  color: theme.palette.brand.gray400,
  cursor: "pointer",
  marginTop: 14,
  flexShrink: 0,
}));

const ToggleRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
});

const BrandSwitch = styled(Switch)(({ theme }: { theme: Theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: theme.palette.brand.coral,
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: theme.palette.brand.coral,
  },
}));

const ToggleLabel = styled(FormControlLabel)(({ theme }: { theme: Theme }) => ({
  "& .MuiFormControlLabel-label": {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.brand.gray900,
  },
  marginLeft: 0,
  marginRight: 0,
}));

// ── Component ─────────────────────────────────────────────────────────────────

const CampaignTemplateDetailsPanelForm: ForwardRefRenderFunction<Ref, FormProps> = (
  { formInitialValues, handleSubmit, mode = "template" },
  ref,
) => {
  const dispatch = useDispatch();
  const isCampaignMode = mode === "campaign";
  const validationSchema = Yup.object().shape({
    ...(!isCampaignMode && { templateName: Yup.string().required("Required field.") }),
    name: Yup.string()
      .required("Required field.")
      .test(
        "pixel-length-check",
        (value) =>
          `Campaign name is too long. Maximum length is ${MAX_CAMPAIGN_NAME_LENGTH} characters (current: ${
            value?.value?.length || 0
          }).`,
        (value) => {
          if (!value) return true;
          return `${value} Pixel`.length <= 100;
        },
      ),
    mediaType: Yup.string().required("Required field."),
  });

  return (
    <Formik
      initialValues={formInitialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur
      validateOnChange={false}
    >
      {({ values, setFieldValue, handleChange, handleBlur }) => (
        <Form>
          <FormColumn>
            {!isCampaignMode && (
              <FieldGroup>
                <FieldLabel>
                  Template Name <RequiredMark>*</RequiredMark>
                </FieldLabel>
                <InputSkin>
                  <Field
                    as={BaseInput}
                    name="templateName"
                    variant="outlined"
                    onChange={handleChange}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      handleBlur(e);
                      dispatch(setCampaignTemplateName(e.target.value));
                    }}
                  />
                </InputSkin>
                <Box style={{ minHeight: 15 }}>
                  <FormFieldError name="templateName" />
                </Box>
              </FieldGroup>
            )}

            <FieldGroup>
              <FieldLabel>
                Campaign Name <RequiredMark>*</RequiredMark>
              </FieldLabel>
              <InputSkin>
                <Field
                  as={BaseInput}
                  name="name"
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    handleBlur(e);
                    dispatch(setCampaignBasicDetails({ name: e.target.value }));
                  }}
                />
              </InputSkin>
              <Box style={{ minHeight: 15 }}>
                <FormFieldError name="name" />
              </Box>
            </FieldGroup>

            <MediaRow>
              <FieldGroup>
                <FieldLabel>
                  Media Type <RequiredMark>*</RequiredMark>
                </FieldLabel>
                <FieldRow>
                  <InputSkin>
                    <Field
                      as={Select}
                      value={values.mediaType}
                      name="mediaType"
                      options={mediaTypeOptions}
                      onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                        const val = e.target.value as string;
                        setFieldValue("mediaType", val);
                        dispatch(setCampaignBasicDetails({ isVideoCampaign: val === "video" }));
                      }}
                    />
                  </InputSkin>
                  <Tooltip title={tooltips.mediaType}>
                    <TooltipIcon />
                  </Tooltip>
                </FieldRow>
                <Box style={{ minHeight: 15 }}>
                  <FormFieldError name="mediaType" />
                </Box>
              </FieldGroup>

              {/* paddingTop aligns toggle with the input below the left-cell label */}
              <div style={{ paddingTop: 18 }}>
                <ToggleRow>
                  <ToggleLabel
                    control={
                      <BrandSwitch
                        checked={values.showInHubspot}
                        onChange={(e) => setFieldValue("showInHubspot", e.target.checked)}
                      />
                    }
                    label="Sales Enabled Campaign"
                  />
                  <Tooltip title={tooltips.showInHubspot}>
                    <TooltipIcon style={{ marginTop: 0 }} />
                  </Tooltip>
                </ToggleRow>
              </div>
            </MediaRow>

            {values.showInHubspot && (
              <FieldGroup>
                <FieldLabel>Max Companies</FieldLabel>
                <FieldRow>
                  <InputSkin>
                    <Field
                      as={BaseInput}
                      value={values.maxCompanies}
                      name="maxCompanies"
                      variant="outlined"
                      type="number"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </InputSkin>
                  <Tooltip title={tooltips.maxCompanies}>
                    <TooltipIcon />
                  </Tooltip>
                </FieldRow>
                <Box style={{ minHeight: 15 }}>
                  <FormFieldError name="maxCompanies" />
                </Box>
              </FieldGroup>
            )}

            <FieldGroup>
              <FieldLabel>Description</FieldLabel>
              <InputSkin>
                <Field as={BaseInput} name="description" variant="outlined" rows={2} multiline />
              </InputSkin>
              <Box style={{ minHeight: 15 }}>
                <FormFieldError name="description" />
              </Box>
            </FieldGroup>
          </FormColumn>

          <button ref={ref} type="submit" aria-label="Submit" style={{ display: "none" }} />
        </Form>
      )}
    </Formik>
  );
};

export default React.forwardRef(CampaignTemplateDetailsPanelForm);
